import React from 'react';
import { useHistory } from 'react-router-dom';

import { PageLayout } from '@/components/templates/PageLayout';
import {
  ArrowBackIcon,
  CloseIcon,
  CmBox,
  CmButton,
  CmCard,
  CmFlex,
  CmHeading,
  CmLoading,
  CmText,
  InfoIcon,
  SendIcon,
} from '@/components/uiParts';
import {
  canUseMosPosResendPage,
  redirect404MosPosResendPage,
} from '@/lib/AccessControl';
import { ActionConfirmModal } from '@/pages/externals/mos-pos-resend/detail/components/ActionConfirmModal';
import { OrderDetailSections } from '@/pages/externals/mos-pos-resend/detail/components/OrderDetailSections';
import { useMosPosOrderDetailPage } from '@/pages/externals/mos-pos-resend/detail/useMosPosOrderDetailPage';
import {
  ACTION_NOTE_TITLE_COLOR,
  CANCEL_BUTTON_BG,
  CONFIRM_BANNER_ACCENT_COLOR,
  CONFIRM_BANNER_BG,
  CONFIRM_BANNER_BORDER_COLOR,
  DetailMode,
  RESEND_BUTTON_BG,
  RESEND_BUTTON_HOVER_BG,
} from '@/pages/externals/mos-pos-resend/service';
import { routes } from '@/router';
import { useManagerAccountMe } from '@/services/AuthEnterprise/hooks';

type Props = {
  mode: DetailMode;
};

/**
 * SCR002 (再送信確認) / SCR003 (取消確認) の共通実装。
 * 表示項目と取得 API は完全に共通で、異なるのはバナー・注意書き・実行ボタン・モーダルの文言のみ。
 */
export const MosPosOrderConfirmPage: React.VFC<Props> = ({ mode }) => {
  /* アクセス制御 */
  const history = useHistory();
  const { managerAccountMe } = useManagerAccountMe();
  redirect404MosPosResendPage(history, managerAccountMe);

  const {
    detail,
    isLoading,
    hasError,
    isRedirecting,
    isSubmitting,
    canExecute,
    modal,
    handlers,
  } = useMosPosOrderDetailPage(mode, canUseMosPosResendPage(managerAccountMe));

  const isResend = mode === 'resend';
  const title = isResend
    ? routes.MosPosResendConfirm.title
    : routes.MosPosCancelConfirm.title;

  // エラーの詳細はトーストで通知し、一覧へ自動で戻す (hook 側)。
  // ローディング表示のままにすると読み込みが続いているように見えるため先に分岐する。
  if (hasError) {
    return (
      <PageLayout title={title}>
        <CmCard mt="25px" alignItems="center">
          <CmText>注文情報を取得できませんでした。一覧画面に戻ります。</CmText>
        </CmCard>
      </PageLayout>
    );
  }

  if (isLoading || detail === undefined) {
    return (
      <PageLayout title={title}>
        <CmCard mt="25px" alignItems="center">
          <CmText mb="12px">注文情報を読み込み中…</CmText>
          <CmLoading />
        </CmCard>
      </PageLayout>
    );
  }

  return (
    <PageLayout title={title}>
      <CmBox mt="25px" pb="40px">
        {/* No.4 一覧に戻る: コンテンツエリア右上の Outline ボタン (小) */}
        <CmFlex justifyContent="space-between" alignItems="center" mb="16px">
          <CmHeading size="lg">
            {isResend ? '再送信確認' : '取消確認'}
          </CmHeading>
          <CmButton
            size="sm"
            variant="outline"
            leftIcon={<ArrowBackIcon />}
            onClick={handlers.handleBack}
          >
            一覧に戻る
          </CmButton>
        </CmFlex>

        {/* No.5 確認バナー: SCR002 / SCR003 とも同じ警告配色 (オレンジ左ボーダー) */}
        <CmCard
          mb="16px"
          bg={CONFIRM_BANNER_BG}
          border="1px solid"
          borderColor={CONFIRM_BANNER_BORDER_COLOR}
          borderLeft="4px solid"
          borderLeftColor={CONFIRM_BANNER_ACCENT_COLOR}
        >
          <CmFlex alignItems="flex-start" gridGap="8px">
            <InfoIcon color={CONFIRM_BANNER_ACCENT_COLOR} mt="2px" />
            <CmBox>
              {isResend ? (
                // テンプレートと同じくボタン名【再送信】だけを太字にする
                <CmText>
                  再送信を行ないます。よろしければ、【
                  <CmText as="span" fontWeight="bold">
                    再送信
                  </CmText>
                  】ボタンをクリックしてください。
                </CmText>
              ) : (
                <>
                  <CmText mb="4px">
                    注文から48時間を経過しているため再送信はできません。【
                    <CmText as="span" fontWeight="bold">
                      取消
                    </CmText>
                    】のみ可能です。
                  </CmText>
                  <CmText>
                    ※「取消」後も、フードデリバリーサービス側の注文はキャンセルされません。
                  </CmText>
                </>
              )}
            </CmBox>
          </CmFlex>
        </CmCard>

        <OrderDetailSections detail={detail} />

        {/* No.58〜60 アクションフッター: 注意書きと操作ボタンを 1 枚のカードに横並び */}
        <CmCard>
          <CmFlex
            justifyContent="space-between"
            alignItems="center"
            gridGap="16px"
            flexWrap="wrap"
          >
            <CmBox maxWidth="55%" fontSize="12px" color="gray.500">
              <CmText fontWeight="bold" color={ACTION_NOTE_TITLE_COLOR}>
                {isResend ? '「再送信」について:' : '「取消」について:'}
              </CmText>
              {isResend ? (
                <CmText fontSize="12px">
                  上記内容でPOS連携を再送信します。再送信は店舗のPOSシステムへ送信されるため、店舗ネットワークの稼働状況をご確認ください。
                </CmText>
              ) : (
                <>
                  <CmText fontSize="12px">
                    注文から48時間を経過した注文は再送信できません。本注文を取消します。
                  </CmText>
                  <CmText fontSize="12px">
                    ※「取消」後も、フードデリバリーサービス側の注文はキャンセルされません。
                  </CmText>
                </>
              )}
            </CmBox>

            <CmFlex gridGap="12px" flexShrink={0}>
              <CmButton
                variant="outline"
                leftIcon={<ArrowBackIcon />}
                onClick={handlers.handleBack}
              >
                戻る
              </CmButton>
              {/* 読み込み完了前・遷移待ちの間は実行ボタンを出さない */}
              {!isRedirecting &&
                canExecute &&
                (isResend ? (
                  // @ts-ignore TS2590
                  <CmButton
                    bg={RESEND_BUTTON_BG}
                    color="white"
                    _hover={{ bg: RESEND_BUTTON_HOVER_BG }}
                    leftIcon={<SendIcon boxSize={3} />}
                    isLoading={isSubmitting}
                    disabled={isSubmitting}
                    onClick={modal.onOpen}
                  >
                    再送信
                  </CmButton>
                ) : (
                  // @ts-ignore TS2590
                  <CmButton
                    colorScheme="red"
                    bg={CANCEL_BUTTON_BG}
                    leftIcon={<CloseIcon boxSize={3} />}
                    isLoading={isSubmitting}
                    disabled={isSubmitting}
                    onClick={modal.onOpen}
                  >
                    取消
                  </CmButton>
                ))}
            </CmFlex>
          </CmFlex>
        </CmCard>
      </CmBox>

      <ActionConfirmModal
        mode={mode}
        webOrderId={detail.web_order_id}
        isOpen={modal.isOpen}
        isSubmitting={isSubmitting}
        onClose={modal.onClose}
        onExecute={handlers.handleExecute}
      />
    </PageLayout>
  );
};
